import { ReactNode } from 'react';
import styled from 'styled-components';

import Tooltip from 'components/shared/Tooltip';

import { formatPercentage } from '../constants';

import { TopEntity } from './useSlideoutSharedData';

type Tone = 'green' | 'red' | 'default';

type StatTileProps = {
  label: string;
  primary: ReactNode;
  addendum?: ReactNode;
  tone?: Tone;
  loading?: boolean;
};

// Vertical stack: label, big primary value, optional small grey addendum underneath.
// Used for every metric panel in the slideout headers (rate tiles, top-by tiles, summary tiles).
export function StatTile({ label, primary, addendum, tone = 'default', loading = false }: StatTileProps) {
  return (
    <Tooltip text={primary} disabled={loading} position='top'>
      <StatBox>
        <StatLabel>{label}</StatLabel>
        <PrimaryValue $tone={tone}>{loading ? '—' : primary}</PrimaryValue>
        {!loading && addendum != null && <Addendum>{addendum}</Addendum>}
      </StatBox>
    </Tooltip>
  );
}

// Three-tile row used by every rate metric drill (Acceptance, Denial): one rate tile plus a
// top-by receiving partner and a top-by plan, both formatted as "X.X% <ratePhrase>".
export function RateMetricTiles({
  rateLabel,
  rateTone,
  rate,
  count,
  total,
  ratePhrase,
  loading,
  topByLoading,
  topReceivingLabel,
  topReceiving,
  topPlanLabel,
  topPlan,
}: {
  rateLabel: string;
  rateTone: Tone;
  rate: number | null;
  count: number;
  total: number;
  ratePhrase: string;
  loading: boolean;
  topByLoading: boolean;
  topReceivingLabel: string;
  topReceiving: TopEntity;
  topPlanLabel: string;
  topPlan: TopEntity;
}) {
  return (
    <>
      <StatTile
        label={rateLabel}
        tone={rateTone}
        loading={loading}
        primary={formatPercentage(rate)}
        addendum={rate != null ? `${count} of ${total} referrals` : undefined}
      />
      <StatTile
        label={topReceivingLabel}
        loading={topByLoading}
        primary={topReceiving?.name ?? '—'}
        addendum={topReceiving ? `${formatPercentage(topReceiving.value)} ${ratePhrase}` : undefined}
      />
      <StatTile
        label={topPlanLabel}
        loading={topByLoading}
        primary={topPlan?.name ?? '—'}
        addendum={topPlan ? `${formatPercentage(topPlan.value)} ${ratePhrase}` : undefined}
      />
    </>
  );
}

export const TilesGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns = 3 }) => $columns}, 1fr);
  gap: 12px;
`;

const StatBox = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
  padding: 20px 22px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  // Allow children to shrink below their content width so ellipsis truncation actually kicks in
  // when the tile is in a grid column narrower than the value/addendum text.
  min-width: 0;
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.black75};
  letter-spacing: 0.18px;
`;

const PrimaryValue = styled.span<{ $tone: Tone }>`
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1;
  color: ${({ theme, $tone }) => {
    if ($tone === 'green') return theme.colors.accentGreen;
    if ($tone === 'red') return theme.colors.accentRed;
    return theme.colors.black;
  }};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Addendum = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.black50};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
